import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, RefreshCw, UserPlus, Trash2, AlertCircle, Shield } from 'lucide-react';
import { z } from 'zod';

const emailSchema = z.string().email('Invalid email format');

interface AllowedEmail {
  id: string;
  email: string;
  added_by: string | null;
  notes: string | null;
  created_at: string;
}

const AdminEmailAllowlist = () => {
  const [allowedEmails, setAllowedEmails] = useState<AllowedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchAllowedEmails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('allowed_emails')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAllowedEmails(data || []);
    } catch (error: any) {
      console.error('Error fetching allowed emails:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load allowed emails',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllowedEmails();
  }, []);

  const validateEmail = (email: string): boolean => {
    try {
      emailSchema.parse(email);
      return true;
    } catch {
      return false;
    }
  };

  const addAllowedEmail = async () => {
    if (!newEmail.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Email address is required',
        variant: 'destructive',
      });
      return;
    }

    if (!validateEmail(newEmail)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsAdding(true);
      
      // Get current admin email
      const { data: { user } } = await supabase.auth.getUser();
      const adminEmail = user?.email || 'system';

      const { error } = await supabase
        .from('allowed_emails')
        .insert([
          {
            email: newEmail.trim().toLowerCase(),
            added_by: adminEmail,
            notes: notes.trim() || null,
          },
        ]);

      if (error) {
        if (error.code === '23505') {
          throw new Error('This email is already in the allowlist');
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: `${newEmail} has been added to the allowlist`,
      });

      setNewEmail('');
      setNotes('');
      fetchAllowedEmails();
    } catch (error: any) {
      console.error('Error adding email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add email to allowlist',
        variant: 'destructive',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const removeAllowedEmail = async (email: string) => {
    try {
      const { error } = await supabase
        .from('allowed_emails')
        .delete()
        .eq('email', email);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `${email} has been removed from the allowlist`,
      });

      fetchAllowedEmails();
    } catch (error: any) {
      console.error('Error removing email:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove email from allowlist',
        variant: 'destructive',
      });
    } finally {
      setDeleteEmail(null);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isAdding) {
      addAllowedEmail();
    }
  };

  return (
    <Card className="w-full bg-white/10 backdrop-blur-md border-white/20">
      <CardHeader className="border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl font-bold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Email Allowlist Management
            </CardTitle>
            <CardDescription className="text-muted-foreground mt-2">
              Control which email addresses can access the website
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchAllowedEmails}
            disabled={loading}
            className="border-border hover:bg-accent"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {/* Add New Email Section */}
        <div className="space-y-4 p-6 border border-border rounded-lg bg-accent/50 backdrop-blur-sm">
          <div className="space-y-2">
            <Label htmlFor="newEmail" className="text-foreground font-medium">Email Address</Label>
            <Input
              id="newEmail"
              type="email"
              placeholder="user@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isAdding}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-foreground font-medium">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this user..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isAdding}
              rows={3}
              className="bg-background border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>
          <Button 
            onClick={addAllowedEmail} 
            disabled={isAdding} 
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isAdding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                Add to Allowlist
              </>
            )}
          </Button>
        </div>

        {/* Allowed Emails List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg text-foreground">Allowed Emails ({allowedEmails.length})</h3>
          {loading ? (
            <div className="flex items-center justify-center p-12 bg-accent/30 rounded-lg border border-border">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : allowedEmails.length === 0 ? (
            <div className="text-center p-12 bg-accent/30 rounded-lg border border-border">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No emails in the allowlist yet</p>
            </div>
          ) : (
            <div className="border border-border rounded-lg bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border bg-accent/50 hover:bg-accent/50">
                    <TableHead className="text-foreground font-semibold">Email</TableHead>
                    <TableHead className="text-foreground font-semibold">Added By</TableHead>
                    <TableHead className="text-foreground font-semibold">Notes</TableHead>
                    <TableHead className="text-foreground font-semibold">Added Date</TableHead>
                    <TableHead className="text-right text-foreground font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allowedEmails.map((item) => (
                    <TableRow key={item.id} className="border-b border-border hover:bg-accent/30">
                      <TableCell className="font-medium text-foreground">{item.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {item.added_by || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {item.notes || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteEmail(item.email)}
                          className="hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="p-5 bg-amber-500/10 border border-amber-500/30 rounded-lg backdrop-blur-sm">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm space-y-1.5">
              <p className="font-semibold text-amber-900 dark:text-amber-100">
                Important Security Information
              </p>
              <ul className="text-amber-800 dark:text-amber-200 space-y-1 list-disc list-inside leading-relaxed">
                <li>Only emails in this allowlist can access the website</li>
                <li>Removing an email will prevent that user from accessing the site</li>
                <li>All changes are logged for security purposes</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteEmail} onOpenChange={() => setDeleteEmail(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Remove Email from Allowlist</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to remove <strong className="text-foreground">{deleteEmail}</strong> from the allowlist?
              This user will no longer be able to access the website.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border hover:bg-accent">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEmail && removeAllowedEmail(deleteEmail)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AdminEmailAllowlist;
